import { useRef, useState, useLayoutEffect, useCallback } from "react";
import styles from "./Grid.module.css";

const computeOptimalLayout = (
  count: number,
  availableW: number,
  availableH: number,
  gap: number,
  ratio: number,
) => {
  let bestCols = 1;
  let bestCellW = 0;
  let bestCellH = 0;

  for (let cols = 1; cols <= count; cols++) {
    const rows = Math.ceil(count / cols);
    const maxCellW = (availableW - gap * (cols - 1)) / cols;
    const maxCellH = (availableH - gap * (rows - 1)) / rows;

    let cellW = maxCellW;
    let cellH = cellW / ratio;

    if (cellH > maxCellH) {
      cellH = maxCellH;
      cellW = cellH * ratio;
    }

    if (cellW > bestCellW) {
      bestCellW = cellW;
      bestCellH = cellH;
      bestCols = cols;
    }
  }

  const bestRows = Math.ceil(count / bestCols);
  return { cols: bestCols, rows: bestRows, cellW: bestCellW, cellH: bestCellH };
};

export const Grid = ({
  elements,
  style,
  ratio,
}: {
  elements: React.ReactNode[];
  style?: React.CSSProperties;
  ratio?: number;
}) => {
  const hasRatio = ratio != null;
  const containerRef = useRef<HTMLDivElement>(null);
  const [layout, setLayout] = useState<{ cols: number; rows: number; cellW: number; cellH: number } | null>(null);

  const computeSize = useCallback(() => {
    const el = containerRef.current;
    if (!el || !hasRatio) return;

    const computedStyle = getComputedStyle(el);
    const gap = parseFloat(computedStyle.gap) || 0;
    const paddingX = parseFloat(computedStyle.paddingLeft) + parseFloat(computedStyle.paddingRight) || 0;
    const paddingY = parseFloat(computedStyle.paddingTop) + parseFloat(computedStyle.paddingBottom) || 0;

    const availableW = el.clientWidth - paddingX;
    const availableH = el.clientHeight - paddingY;

    setLayout(computeOptimalLayout(elements.length, availableW, availableH, gap, ratio!));
  }, [elements.length, ratio, hasRatio]);

  useLayoutEffect(() => {
    computeSize();
    const el = containerRef.current;
    if (!el || !hasRatio) return;
    const observer = new ResizeObserver(computeSize);
    observer.observe(el);
    return () => observer.disconnect();
  }, [computeSize, hasRatio]);

  if (!hasRatio) {
    return (
      <div
        className={styles.classicGrid}
        style={style}
      >
        {elements.map((el, index) => (
          <div key={index}>
            {el}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={styles.grid}
      style={style}
    >
      {layout && (
        <div
          className={styles.content}
          style={{
            gridTemplateColumns: `repeat(${layout.cols}, ${layout.cellW}px)`,
            gridTemplateRows: `repeat(${layout.rows}, ${layout.cellH}px)`,
          }}
        >
          {elements.map((el, index) => (
            <div key={index} className={styles.cell}>
              {el}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
